// src/features/collaboration/CollaborationLayer.js
import React, { useState, useEffect } from 'react';
import { Box, Container, Grid, useMediaQuery, useTheme, Drawer, Fab } from '@mui/material';
import PostFeed from './components/PostFeed';
import NetworkSidebar from './components/NetworkSidebar';
import MessageCenter from './components/MessageCenter';
import CreatePostModal from './components/CreatePostModal';
import { 
  subscribeToPostFeed, 
  createPost,
  updatePost,
  subscribeToConnections
} from './services/collaborationService';
import { useAuth } from '../../contexts/AuthContext';
import { THEME_COLORS } from '../../styles/theme';
import { Message as MessageIcon, Group as GroupIcon } from '@mui/icons-material';

const CollaborationLayer = () => {
  const [posts, setPosts] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [initialPostType, setInitialPostType] = useState('post');
  const [editingPost, setEditingPost] = useState(null);
  const { userProfile, currentUser } = useAuth();
  
  // Mobile drawer states
  const [networkDrawerOpen, setNetworkDrawerOpen] = useState(false);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  useEffect(() => {
    let unsubscribePosts;
    let unsubscribeConnections;
    
    if (currentUser) {
      // Subscribe to posts feed
      setLoading(true);
      unsubscribePosts = subscribeToPostFeed(
        { limitCount: 20 },
        (newPosts) => {
          setPosts(newPosts);
          setLoading(false);
        }
      );
      
      // Subscribe to connections
      unsubscribeConnections = subscribeToConnections(
        currentUser.uid,
        (newConnections) => {
          setConnections(newConnections);
        }
      );
    }
    
    // Cleanup subscriptions on component unmount
    return () => {
      if (unsubscribePosts) unsubscribePosts();
      if (unsubscribeConnections) unsubscribeConnections();
    };
  }, [currentUser]);
  
  const handleCreatePost = (type = 'post') => {
    setInitialPostType(type);
    setEditingPost(null);
    setIsPostModalOpen(true);
  };

  const handleEditPost = (post) => {
    if (!post) return;
    setEditingPost(post);
    setInitialPostType(post.postType || 'post');
    setIsPostModalOpen(true);
  };
  
  const handleSubmitPost = async (postData) => {
    try {
      if (currentUser) {
        console.log('Post submission by', currentUser);
        await createPost(currentUser.uid, postData);
        setIsPostModalOpen(false);
        setEditingPost(null);
      }
    } catch (error) {
      console.error("Error creating post:", error);
    }
  };

  const handleUpdatePost = async (postData) => {
    try {
      if (postData?.id) {
        await updatePost(postData.id, postData);
        setIsPostModalOpen(false);
        setEditingPost(null);
      }
    } catch (error) {
      console.error("Error updating post:", error);
    }
  };
  
  const toggleNetworkDrawer = () => {
    setNetworkDrawerOpen(!networkDrawerOpen);
    if (messageDrawerOpen) setMessageDrawerOpen(false);
  };

  const toggleMessageDrawer = () => {
    setMessageDrawerOpen(!messageDrawerOpen);
    if (networkDrawerOpen) setNetworkDrawerOpen(false);
  };
  
  return (
    <Container 
      maxWidth="lg" 
      sx={{ mt: 4, mb: 4 }} 
      disableGutters={isMobile}  // removes container gutters in mobile
    >
      {/* Desktop layout */}
      {!isMobile && (
        <Grid container spacing={2}>
          <Grid item style={{ width: '22%' }}>
            <NetworkSidebar 
              connections={connections} 
              onCreatePost={handleCreatePost}
            />
          </Grid>
          
          <Grid item style={{ width: '50%' }}>
            <PostFeed 
              posts={posts}
              loading={loading}
              onCreatePost={handleCreatePost}
              onEditPost={handleEditPost}
            />
          </Grid>
          
          <Grid item style={{ width: '25%' }}>
            <MessageCenter 
              user={currentUser ? { id: currentUser.uid } : null}
            />
          </Grid>
        </Grid>
      )}
      
      {/* Mobile layout */}
      {isMobile && (
        <Box sx={{ width: '100%', mb: 8 }}>
          <Box sx={{ m: 0, p: 0 }}>
            <PostFeed 
              posts={posts}
              loading={loading}
              onCreatePost={handleCreatePost}
              onEditPost={handleEditPost}
            />
          </Box>
          {/* Mobile navigation */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              zIndex: 1100
            }}
          >
            <Fab
              color="primary"
              aria-label="network"
              onClick={toggleNetworkDrawer}
              sx={{ bgcolor: THEME_COLORS.primary }}
            >
              <GroupIcon />
            </Fab>
            <Fab
              color="primary"
              aria-label="messages"
              onClick={toggleMessageDrawer}
              sx={{ bgcolor: THEME_COLORS.primary }}
            >
              <MessageIcon />
            </Fab>
          </Box>
          
          {/* Mobile drawers */}
          <Drawer
            anchor="right"
            open={networkDrawerOpen}
            onClose={() => setNetworkDrawerOpen(false)}
          >
            <Box sx={{ width: 300, p: 2, height: '100%', overflow: 'auto' }}>
              <NetworkSidebar 
                connections={connections} 
                onCreatePost={handleCreatePost}
              />
            </Box>
          </Drawer>
          
          <Drawer
            anchor="right"
            open={messageDrawerOpen}
            onClose={() => setMessageDrawerOpen(false)}
          >
            <Box sx={{ width: 300, p: 2, height: '100%', overflow: 'auto' }}>
              <MessageCenter 
                user={currentUser ? { id: currentUser.uid } : null}
              />
            </Box>
          </Drawer>
        </Box>
      )}
      
      <CreatePostModal 
        open={isPostModalOpen} 
        onClose={() => { setIsPostModalOpen(false); setEditingPost(null); }} 
        onSubmit={handleSubmitPost}
        onUpdate={handleUpdatePost}
        initialPostType={initialPostType}
        initialPost={editingPost}
      />
    </Container>
  );
};
export default CollaborationLayer;
